// Pure helpers of the n8n triggers editor: a local mirror of the server's {{a.b}} template rules
// (server/lib/text.js renderTemplate: own data properties only, prototype paths refused, missing →
// '', objects/arrays as JSON, values never re-scanned), the sample-payload JSON checks, the
// placeholder help, and the ready-to-paste n8n / curl snippets. DOM-free.

import { MONTHS, DAYS } from '../../i18n/fr.js';

/** Same placeholder syntax as the server: {{payload.path.to.value}}. */
export const TEMPLATE_RE = /\{\{\s*([A-Za-z0-9_$-]+(?:\.[A-Za-z0-9_$-]+)*)\s*\}\}/g;
const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
/** The server cuts every inserted value to this many characters (server/triggers/service.js). */
export const MAX_VALUE_IN_TEMPLATE = 4000;
/** Port of the office on the Mac mini (SPEC §1): n8n calls it locally. */
export const DEFAULT_HOOK_ORIGIN = 'http://127.0.0.1:4520';

/**
 * Reads `a.b.0.c` from `obj` using own data properties only; prototype segments give undefined.
 * @param {unknown} obj
 * @param {string|string[]} path
 * @returns {unknown}
 */
export function lookupPath(obj, path) {
  const parts = Array.isArray(path) ? path : String(path ?? '').split('.');
  let cur = obj;
  for (const key of parts) {
    if (key === '' || FORBIDDEN_KEYS.has(key)) return undefined;
    if (cur === null || typeof cur !== 'object') return undefined;
    const desc = Object.getOwnPropertyDescriptor(cur, key);
    if (!desc || !('value' in desc)) return undefined;
    cur = desc.value;
  }
  return cur;
}

function templateValue(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  if (typeof v === 'object') {
    try { return JSON.stringify(v) ?? ''; } catch { return ''; }
  }
  return '';
}

function cut(str, n) {
  if (!Number.isFinite(n) || str.length <= n) return str;
  let end = Math.max(0, n - 1);
  const code = str.charCodeAt(end - 1);
  if (code >= 0xd800 && code <= 0xdbff) end -= 1; // never split a surrogate pair
  return `${str.slice(0, end)}…`;
}

/**
 * Renders a prompt template like the server does when n8n calls the hook (each inserted value is
 * cut to `maxValueLength`, like the server's MAX_VALUE_IN_TEMPLATE).
 * @param {unknown} tpl
 * @param {object} vars usually { payload, now }
 * @param {{maxValueLength?: number}} [opts]
 * @returns {string}
 */
export function renderTemplate(tpl, vars, { maxValueLength = MAX_VALUE_IN_TEMPLATE } = {}) {
  const source = tpl == null ? '' : String(tpl);
  return source.replace(TEMPLATE_RE, (_, path) => cut(templateValue(lookupPath(vars, path)), maxValueLength));
}

/**
 * The value of {{now}} as the server writes it: "jeudi 1er octobre 2026 à 08:00" (local time).
 * @param {number|Date} [ms]
 * @returns {string}
 */
export function frenchNow(ms = Date.now()) {
  const d = ms instanceof Date ? ms : new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getDate() === 1 ? '1er' : String(d.getDate());
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${DAYS[d.getDay()]} ${day} ${MONTHS[d.getMonth()]} ${d.getFullYear()} à ${hh}:${mm}`;
}

/**
 * The placeholder paths of a template, in order of first appearance, without duplicates.
 * @param {string} tpl
 * @returns {string[]}
 */
export function templatePaths(tpl) {
  const out = [];
  for (const m of String(tpl ?? '').matchAll(TEMPLATE_RE)) if (!out.includes(m[1])) out.push(m[1]);
  return out;
}

/**
 * Live preview of a template with a sample payload: the rendered text plus the placeholders that
 * found nothing (they become empty on the server too).
 * @param {string} tpl
 * @param {object} payload
 * @param {{now?: string}} [opts] value of {{now}} (the server passes the time of the call, in French)
 * @returns {{text: string, missing: string[], used: string[]}}
 */
export function previewTemplate(tpl, payload, { now = frenchNow() } = {}) {
  const vars = { payload: payload ?? {}, now };
  const used = templatePaths(tpl);
  const missing = used.filter((p) => {
    const v = lookupPath(vars, p);
    return v === undefined || v === null;
  });
  return { text: renderTemplate(tpl, vars), missing, used };
}

/**
 * Parses the sample payload typed in the editor: it must be a JSON object (the body n8n sends).
 * @param {string} text
 * @returns {{value: object|null, error: string|null}}
 */
export function parseJsonObject(text) {
  const src = String(text ?? '').trim();
  if (!src) return { value: {}, error: null };
  let v;
  try {
    v = JSON.parse(src);
  } catch (err) {
    const pos = /position (\d+)/.exec(String(err?.message || ''));
    let where = '';
    if (pos) {
      const upto = src.slice(0, Number(pos[1]));
      const line = upto.split('\n').length;
      where = ` (ligne ${line})`;
    }
    return { value: null, error: `JSON invalide${where} : vérifiez les guillemets, virgules et accolades.` };
  }
  if (v === null || typeof v !== 'object' || Array.isArray(v)) {
    return { value: null, error: 'Le payload doit être un objet JSON : { "clé": "valeur", … }.' };
  }
  return { value: v, error: null };
}

/**
 * Leaf paths of a payload as placeholders ("payload.title", "payload.client.email",
 * "payload.items.0.name"), for the placeholder help. Bounded in depth and count.
 * @param {object} obj
 * @param {{max?: number, maxDepth?: number, prefix?: string}} [opts]
 * @returns {string[]}
 */
export function payloadPaths(obj, { max = 40, maxDepth = 4, prefix = 'payload' } = {}) {
  const out = [];
  const walk = (v, path, depth) => {
    if (out.length >= max) return;
    if (v !== null && typeof v === 'object' && depth < maxDepth) {
      const keys = Array.isArray(v) ? (v.length ? ['0'] : []) : Object.keys(v).filter((k) => /^[A-Za-z0-9_$-]+$/.test(k) && !FORBIDDEN_KEYS.has(k));
      if (!keys.length) { out.push(path); return; }
      for (const k of keys) walk(v[k], `${path}.${k}`, depth + 1);
      return;
    }
    out.push(path);
  };
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    for (const k of Object.keys(obj)) {
      if (!/^[A-Za-z0-9_$-]+$/.test(k) || FORBIDDEN_KEYS.has(k)) continue;
      walk(obj[k], `${prefix}.${k}`, 1);
    }
  }
  return out;
}

/**
 * A sample payload built from the template's {{payload.…}} placeholders, so a new trigger can be
 * tested at once: {{payload.client.email}} → { client: { email: "exemple" } }.
 * @param {string} tpl
 * @returns {object}
 */
export function skeletonFromTemplate(tpl) {
  const root = {};
  for (const path of templatePaths(tpl)) {
    const parts = path.split('.');
    if (parts[0] !== 'payload' || parts.length < 2 || parts.some((p) => FORBIDDEN_KEYS.has(p))) continue;
    let cur = root;
    for (let i = 1; i < parts.length; i += 1) {
      const key = parts[i];
      const last = i === parts.length - 1;
      if (last) {
        if (!(key in cur) || typeof cur[key] !== 'object') cur[key] = key === 'externalRef' || key === 'id' ? 'ref-123' : 'exemple';
      } else {
        if (cur[key] === null || typeof cur[key] !== 'object') cur[key] = {};
        cur = cur[key];
      }
    }
  }
  return root;
}

/**
 * The address n8n calls on the Mac mini: http://127.0.0.1:4520/hooks/<id>. When the page itself is
 * opened on the machine (localhost), its own port is used.
 * @param {string} id trigger id
 * @param {{hostname?: string, port?: string, protocol?: string}|null} [loc] window.location
 * @returns {string}
 */
export function hookUrl(id, loc = null) {
  let origin = DEFAULT_HOOK_ORIGIN;
  const host = String(loc?.hostname || '').replace(/^\[|\]$/g, '');
  if (['localhost', '127.0.0.1', '::1'].includes(host) && /^\d{2,5}$/.test(String(loc?.port || ''))) {
    origin = `http://127.0.0.1:${loc.port}`;
  }
  return `${origin}/hooks/${encodeURIComponent(String(id ?? ''))}`;
}

/** Pretty JSON for the snippets (2 spaces); '{}' for nothing. */
export function prettyJson(v) {
  try { return JSON.stringify(v ?? {}, null, 2) ?? '{}'; } catch { return '{}'; }
}

/**
 * A curl example that never puts the secret in the shell history (read -s).
 * @param {string} url
 * @param {object} payload
 * @returns {string}
 */
export function curlExample(url, payload) {
  const body = JSON.stringify(payload ?? {}).replace(/'/g, "'\\''");
  return [
    'read -s SECRET   # collez le secret du déclencheur, puis Entrée',
    `curl -sS -X POST '${url}' \\`,
    '  -H "Authorization: Bearer $SECRET" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${body}'`,
  ].join('\n');
}

/**
 * The settings of n8n's HTTP Request node, row by row (label, value, whether to show it as code).
 * @param {string} url
 * @param {{wait?: boolean}} [opts] wait: the trigger is autonomous, ?wait= returns the result
 * @returns {Array<{label: string, value: string, code?: boolean}>}
 */
export function n8nNodeSettings(url, { wait = false } = {}) {
  const rows = [
    { label: 'Method', value: 'POST', code: true },
    { label: 'URL', value: wait ? `${url}?wait=60` : url, code: true },
    { label: 'Authentication', value: 'Generic Credential Type → Header Auth' },
    { label: 'Header Auth (identifiant)', value: 'Name : Authorization · Value : Bearer <secret>' },
    { label: 'Send Body', value: 'activé · Body Content Type : JSON · Specify Body : Using JSON' },
  ];
  if (wait) rows.push({ label: 'Options → Timeout', value: '70000 (ms, au-dessus de l’attente)' });
  return rows;
}

/** True for an absolute http(s) URL (the server then checks its callback allowlist). */
export function isHttpUrl(s) {
  const v = String(s ?? '').trim();
  if (!/^https?:\/\/[^\s/]+/i.test(v)) return false;
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !!u.hostname;
  } catch {
    return false;
  }
}

/** "••••ab12" from a secret hint (last 4 characters), '' when unknown. */
export function maskedHint(hint) {
  const h = String(hint ?? '').replace(/[^\w-]/g, '').slice(-4);
  return h ? `••••${h}` : '';
}

/**
 * Inserts `snippet` into `value` at the selection [start, end); returns the new value and caret.
 * @returns {{value: string, caret: number}}
 */
export function insertAt(value, snippet, start, end = start) {
  const v = String(value ?? '');
  const s = Math.max(0, Math.min(v.length, Number.isInteger(start) ? start : v.length));
  const e = Math.max(s, Math.min(v.length, Number.isInteger(end) ? end : s));
  return { value: v.slice(0, s) + snippet + v.slice(e), caret: s + snippet.length };
}
